import { z } from 'zod';
import { TenantRegionSchema } from './tenantRegion';
import { isEuCountry } from './enums';

/**
 * Contrato do self-service multiplataforma: canal de venda, zona de preço por
 * país, a chave do preço na Stripe e as bordas dos callables do
 * Android (Google Play / Stripe) e do web (Stripe).
 *
 * Origem: #969 — 2026-10-14 13:31 +0200.
 *
 * Por quê: o iOS vende por IAP desde a #942. O owner estendeu a regra
 * (2026-10-14): no Android e no web, quem cria conta e não tem estabelecimento
 * também vai para o pagamento, com 7 dias grátis e todos os módulos. O Android
 * cobra pelo Google Play ou pela Stripe (User Choice Billing); o web, pela
 * Stripe — em TODOS os países, inclusive o BR (o Asaas cobra o cartão na hora
 * e não comporta o trial). O preço é por zona (BR, UE, resto) e o país é
 * DETECTADO pelo servidor (IP; sem resultado, localização do aparelho), nunca
 * escolhido pelo usuário. Venda direta do EtiquetaChef, sem comissão.
 *
 * Contrato: tudo aqui é puro (sem I/O). A reserva, o OTP de e-mail e os
 * motivos de erro continuam em `selfServiceIap.ts` e valem para os três canais.
 *
 * Usado por: Functions `apps/functions/src/selfService/*` e `play/*`
 * (detecção de país, checkout Stripe, ativação Play, RTDN, preço);
 * `iap/selfServiceProvisioning.ts` (canal); web `apps/app` (paywall `/subscribe`);
 * Android (espelho manual em `domain/selfservice/`).
 *
 * Arquitetura: preço congelado por tenant — o preço da loja vale só para
 * compras NOVAS; o valor de quem assinou fica em `billing/subscription`.
 * País vem do servidor (IP / coordenadas / `regionCode` da Play).
 *
 * Issues: #969 (este canal), #942 (iOS, mesmo desenho), #679 (venda assistida,
 * que continua igual).
 */

// ---------- Canal de venda ----------

/**
 * Por onde o tenant self-service foi vendido — gravado em
 * `tenants/{tid}.self_service_origin.channel` e no e-mail para vendas.
 *
 * Origem: #969 — 2026-10-14 13:31 +0200. `APP_STORE_IAP` já era gravado
 * como literal pela #942; o valor não muda.
 */
export const SelfServiceChannel = {
  /** iOS, In-App Purchase da App Store (#942). */
  APP_STORE_IAP: 'APP_STORE_IAP',
  /** Android, Google Play Billing. */
  GOOGLE_PLAY: 'GOOGLE_PLAY',
  /** Web `apps/app`, Stripe Checkout. */
  STRIPE_WEB: 'STRIPE_WEB',
  /** Android, alternativa do User Choice Billing → Stripe Checkout na Custom Tab. */
  STRIPE_ANDROID: 'STRIPE_ANDROID',
} as const;
export type SelfServiceChannel = (typeof SelfServiceChannel)[keyof typeof SelfServiceChannel];
export const ALL_SELF_SERVICE_CHANNELS = Object.values(SelfServiceChannel) as [
  SelfServiceChannel,
  ...SelfServiceChannel[],
];
export const SelfServiceChannelSchema = z.enum(ALL_SELF_SERVICE_CHANNELS);

/** Canais cobrados pela Stripe (checkout hospedado). */
export const STRIPE_SELF_SERVICE_CHANNELS = [
  SelfServiceChannel.STRIPE_WEB,
  SelfServiceChannel.STRIPE_ANDROID,
] as const;
export const StripeSelfServiceChannelSchema = z.enum(STRIPE_SELF_SERVICE_CHANNELS);
export type StripeSelfServiceChannel = z.infer<typeof StripeSelfServiceChannelSchema>;

/** Trial grátis dos três canais (owner, 2026-10-12 no iOS; 2026-10-14 nos demais). */
export const SELF_SERVICE_TRIAL_DAYS = 7;

// ---------- Finalidade da compra (primeiro estabelecimento ou adicional) ----------

/**
 * Para que a conta está assinando: o PRIMEIRO estabelecimento (conta sem
 * vínculo, fluxo da #942/#969) ou um estabelecimento ADICIONAL (OWNER de
 * venda direta que abre mais um pelo menu Mais → "Criar novo
 * estabelecimento").
 *
 * Origem: #970 — 2026-10-14 16:58 +0200.
 *
 * Por quê: as duas compras têm portas e preços de entrada diferentes, e quem
 * decide é o servidor. O primeiro exige conta SEM vínculo (`already_member`)
 * e ganha 7 dias grátis. O adicional exige um vínculo OWNER ativo e nenhum
 * tenant de parceiro (`partner_customer`), e é cobrado na hora, sem trial
 * (owner, 2026-10-14). A finalidade fica gravada na reserva
 * (`selfServiceReservations/{id}.purpose`), e o checkout e a ativação leem de
 * lá, nunca do cliente.
 *
 * Contrato: ausente = `FIRST`. Reserva e cliente antigos (#942/#969) não
 * mandam o campo e não mudam.
 *
 * Usado por: Functions `reserveSelfServiceTenant`,
 * `detectSelfServiceCountry`, `createSelfServiceCheckout` e o provisionamento
 * (`self_service_origin.purpose`); web `SubscribePage`; Android
 * `PaywallViewModel`; iOS `SelfServicePaywallViewModel` (e, #978,
 * `TenantSuspendedView` → paywall no modo reassinatura).
 *
 * `RESUBSCRIBE` (#978, 2026-10-16 18:05 +0200): o OWNER de um estabelecimento
 * suspenso porque a assinatura da App Store acabou assina de novo pelo app, e
 * a compra RELIGA o mesmo estabelecimento (não cria outro). Nasceu da App
 * Review (diretriz 2.1, build 1.0(59)): o revisor pede conta com assinatura
 * expirada e precisa chegar à compra. Desde a #980 (2026-10-19 16:55 +0200)
 * vale também no Android: `GOOGLE_PLAY` e `STRIPE_ANDROID` religam assinatura
 * encerrada da Play OU da App Store (o tenant passa a ser cobrado pelo canal
 * novo); `APP_STORE_IAP` só religa a da App Store; `STRIPE_WEB` ainda recusa
 * (o web não tem a tela). A tabela vive em `resubscribeRefusal`
 * (`apps/functions/src/iap/selfServiceAccount.ts`).
 *
 * Issues: #970 (estabelecimento adicional), #969, #942, #978 (reassinatura),
 * #980 (reassinatura no Android).
 */
export const SelfServicePurpose = {
  /** Primeiro estabelecimento da conta (conta sem vínculo). */
  FIRST: 'FIRST',
  /** Mais um estabelecimento para um OWNER de venda direta. */
  ADDITIONAL: 'ADDITIONAL',
  /**
   * #978 — OWNER de estabelecimento suspenso pela loja assinando de novo;
   * a compra religa o tenant de `target_tenant_id` (canais e provedores na
   * tabela de `resubscribeRefusal`, #980).
   */
  RESUBSCRIBE: 'RESUBSCRIBE',
} as const;
export type SelfServicePurpose = (typeof SelfServicePurpose)[keyof typeof SelfServicePurpose];
export const ALL_SELF_SERVICE_PURPOSES = Object.values(SelfServicePurpose) as [
  SelfServicePurpose,
  ...SelfServicePurpose[],
];
export const SelfServicePurposeSchema = z.enum(ALL_SELF_SERVICE_PURPOSES);

/**
 * Trial da compra: 7 dias só no primeiro estabelecimento (owner, 2026-10-14,
 * #970). Reassinatura (#978) também não tem: quem volta já usou o trial — na
 * App Store quem decide a elegibilidade é a própria Apple, este valor é o do
 * nosso lado (texto da tela, Stripe).
 */
export function selfServiceTrialDays(purpose: SelfServicePurpose): number {
  return purpose === SelfServicePurpose.FIRST ? SELF_SERVICE_TRIAL_DAYS : 0;
}

// ---------- Zona de preço ----------


/**
 * Zona de preço do self-service (owner, 2026-10-14): Brasil em BRL, União
 * Europeia em EUR, resto do mundo em USD. A UE é a lista de `EU_COUNTRIES`
 * (`enums.ts`, 27 países) — fora da zona do euro (PL, SE, DK…) também paga em
 * EUR: a decisão é "UE", não "zona do euro".
 *
 * `EG` (#1053, owner 2026-10-27 16:41 +0100): o Egito sai do `WORLD` e ganha
 * preço próprio em USD. A Apple e a Play já são por país e não precisaram de
 * código; na Stripe o Price é por moeda, então um preço só do Egito exige uma
 * zona própria, com `lookup_key` própria.
 */
export const SelfServicePriceZone = {
  BR: 'BR',
  EU: 'EU',
  /** Egito, em USD, com Price próprio na Stripe (#1053). */
  EG: 'EG',
  WORLD: 'WORLD',
} as const;
export type SelfServicePriceZone = (typeof SelfServicePriceZone)[keyof typeof SelfServicePriceZone];
export const ALL_SELF_SERVICE_PRICE_ZONES = Object.values(SelfServicePriceZone) as [
  SelfServicePriceZone,
  ...SelfServicePriceZone[],
];

/** Moeda cobrada em cada zona. */
export const SELF_SERVICE_ZONE_CURRENCY = {
  BR: 'BRL',
  EU: 'EUR',
  EG: 'USD',
  WORLD: 'USD',
} as const satisfies Record<SelfServicePriceZone, 'BRL' | 'EUR' | 'USD'>;
export type SelfServiceCurrency = (typeof SELF_SERVICE_ZONE_CURRENCY)[SelfServicePriceZone];

// `DEFAULT_SELF_SERVICE_PRICES_CENTS` (R$ 1.250 · € 99 · US$ 99) retirado em
// 2026-10-15 (#970, owner: nada de preço fixo no código). O doc
// `crm_appConfig/selfServicePricing` e a tela do CRM que o editava também saíram
// (2026-10-15 21:19 +0200, #970, owner: "os preços ficam na Apple/Play/Stripe"):
// cada loja é dona do próprio preço — ver `SELF_SERVICE_STRIPE_PRICE_LOOKUP_KEY`.

/**
 * `lookup_key` do Price mensal da Stripe de cada zona — o mesmo nos modos live e
 * sandbox. O servidor busca o Price ATIVO com esta chave e usa o `unit_amount` e
 * a moeda dele; o valor nunca é copiado para o nosso banco.
 *
 * Origem: #970 — 2026-10-15 21:19 +0200 (owner escolheu "cada loja dona do
 * próprio preço" no lugar da tela de preço do CRM).
 *
 * Por quê: Price da Stripe é por MOEDA, não por país (Apple e Play são por
 * país). A regra país → zona fica no código (`selfServicePriceZoneOf`); o valor
 * fica na Stripe. Mudar o preço = criar um Price novo no painel da Stripe e
 * transferir para ele esta `lookup_key` ("Transfer lookup key"); o antigo pode
 * ser arquivado. Sem Price ativo com a chave, a venda recusa com
 * `price_not_configured` — nunca cobra valor inventado.
 *
 * Usado por: Functions `selfService/selfServicePricing.ts` (`stripeZonePrice`)
 * ← `detectSelfServiceCountry`, `createSelfServiceCheckout` e a reserva de
 * preço das lojas (`storeReferencePrice`).
 *
 * Dicas: na zona UE o Price é `tax_behavior: exclusive` (a Stripe Tax soma o
 * IVA — `selfServiceStripeAddsTax`); um Price novo da UE tem de manter isso.
 *
 * Histórico:
 *  - 2026-10-27 16:41 +0100 — (#1053) chave do Egito; Price de USD 99,90 criado
 *    em live e sandbox no mesmo dia.
 */
export const SELF_SERVICE_STRIPE_PRICE_LOOKUP_KEY = {
  BR: 'etiquetachef_allinone_monthly_br',
  EU: 'etiquetachef_allinone_monthly_eu',
  EG: 'etiquetachef_allinone_monthly_eg',
  WORLD: 'etiquetachef_allinone_monthly_world',
} as const satisfies Record<SelfServicePriceZone, string>;

/**
 * A Stripe soma o IVA ao preço da zona (preço "por fora", Stripe Tax)?
 *
 * Origem: #969 — 2026-10-14 21:24 +0200 (owner: "o cliente paga o IVA sempre
 * que eu tiver que pagar").
 *
 * Por quê: na Play e na App Store quem vende perante o fisco da UE é o
 * Google/a Apple, que recolhem o IVA de dentro do preço — lá a UE paga o valor
 * da zona como preço final. Na Stripe quem recolhe é a empresa (SRL italiana):
 * por isso o preço da zona UE é LÍQUIDO e a Stripe Tax soma o IVA devido
 * (Itália 22 %; empresa de outro país da UE com número de IVA válido → reverse
 * charge, sem IVA). Fora da UE não há registro fiscal na Stripe: nada é somado.
 *
 * Usado por: `createSelfServiceCheckout` (liga `automatic_tax` e
 * `tax_id_collection`; o Price da UE na Stripe é `tax_behavior: exclusive`) e o web `SubscribePage` ("+ IVA" ao lado do preço).
 */
export function selfServiceStripeAddsTax(zone: SelfServicePriceZone): boolean {
  return zone === 'EU';
}

/**
 * Zona de preço de um país (alpha-2). Código inválido cai em `WORLD`.
 *
 * Origem: #969 — 2026-10-14 13:31 +0200.
 *
 * Usado por: Functions `priceForCountry` (`selfService/selfServicePricing.ts`),
 * `activatePlayPurchase` e `iap/selfServiceProvisioning.ts` (moeda de
 * referência das lojas).
 *
 * Histórico:
 *  - 2026-10-27 16:41 +0100 — (#1053) Egito (`EG`) com zona própria.
 */
export function selfServicePriceZoneOf(countryAlpha2: string): SelfServicePriceZone {
  const cc = countryAlpha2.trim().toUpperCase();
  if (cc === 'BR') return SelfServicePriceZone.BR;
  if (isEuCountry(cc)) return SelfServicePriceZone.EU;
  if (cc === 'EG') return SelfServicePriceZone.EG;
  return SelfServicePriceZone.WORLD;
}

// ---------- Detecção de país ----------

/** De onde o servidor tirou o país. */
export const CountryDetectionSource = {
  IP: 'IP',
  COORDS: 'COORDS',
  PLAY: 'PLAY',
} as const;
export type CountryDetectionSource = (typeof CountryDetectionSource)[keyof typeof CountryDetectionSource];

/** Coordenadas do aparelho, pedidas só quando o IP não resolve. */
export const DeviceCoordsSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type DeviceCoords = z.infer<typeof DeviceCoordsSchema>;

export const DetectSelfServiceCountryInput = z
  .object({
    coords: DeviceCoordsSchema.optional(),
    /** #970 — ausente = `FIRST`. `ADDITIONAL` troca a porta e devolve `trialDays: 0`. */
    purpose: SelfServicePurposeSchema.optional(),
  })
  .strict();
export type DetectSelfServiceCountryInput = z.infer<typeof DetectSelfServiceCountryInput>;

/**
 * Saída de `detectSelfServiceCountry` (e o que o checkout usa por dentro).
 * `NEEDS_LOCATION`: o IP não deu país e não vieram coordenadas — a tela pede
 * a localização e chama de novo. `NOT_ALLOWED`: país excluído (CN/RU/IR/KP/SY).
 */
export const DetectSelfServiceCountryOutput = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('DETECTED'),
    country: z.string().regex(/^[A-Z]{2}$/),
    source: z.enum([CountryDetectionSource.IP, CountryDetectionSource.COORDS]),
    zone: z.enum(ALL_SELF_SERVICE_PRICE_ZONES),
    currency: z.enum(['BRL', 'EUR', 'USD']),
    amountCents: z.number().int().positive(),
    trialDays: z.number().int().nonnegative(),
  }),
  z.object({ status: z.literal('NEEDS_LOCATION') }),
  z.object({ status: z.literal('NOT_ALLOWED'), country: z.string().regex(/^[A-Z]{2}$/) }),
]);
export type DetectSelfServiceCountryOutput = z.infer<typeof DetectSelfServiceCountryOutput>;

// ---------- Checkout Stripe ----------

/**
 * Entrada de `createSelfServiceCheckout`. Não leva país nem preço: o servidor
 * detecta de novo (o cliente só repassa as coordenadas, se a tela as pediu).
 * `externalTransactionToken` só no Android, quando o usuário escolheu a
 * alternativa no User Choice Billing — o servidor o reporta à Play.
 */
export const CreateSelfServiceCheckoutInput = z
  .object({
    channel: StripeSelfServiceChannelSchema,
    coords: DeviceCoordsSchema.optional(),
    /** Idioma da UI (BCP-47) para o Checkout da Stripe. */
    uiLocale: z.string().min(2).max(35).optional(),
    externalTransactionToken: z.string().min(10).max(4096).optional(),
  })
  .strict();
export type CreateSelfServiceCheckoutInput = z.infer<typeof CreateSelfServiceCheckoutInput>;

export const CreateSelfServiceCheckoutOutput = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('READY'),
    url: z.url(),
    sessionId: z.string().min(1),
    /**
     * #970 — id do tenant que vai nascer (`{CC}-{autoId}`). A tela de retorno
     * espera ESTE vínculo aparecer e o ativa: quem já tem estabelecimento
     * (compra adicional) não pode sair com o tenant antigo ativo. Opcional no
     * schema porque servidor antigo não o devolve.
     */
    plannedTenantId: z.string().min(1).optional(),
    /**
     * #1078 (F2 da #960) — região onde o tenant planejado vai nascer, fixada na reserva
     * (`selfServiceReservations/{id}.planned_region`). Opcional: servidor antigo não a devolve.
     */
    plannedRegion: TenantRegionSchema.optional(),
  }),
  z.object({ status: z.literal('NEEDS_LOCATION') }),
]);
export type CreateSelfServiceCheckoutOutput = z.infer<typeof CreateSelfServiceCheckoutOutput>;

/** `metadata` que a sessão/assinatura da Stripe carrega (lido pelo webhook). */
export const SELF_SERVICE_STRIPE_METADATA = {
  /** Marca a sessão como self-service (desvia do fluxo da venda assistida). */
  PURPOSE_KEY: 'purpose',
  PURPOSE_VALUE: 'self_service',
  RESERVATION_KEY: 'selfServiceReservationId',
  CHANNEL_KEY: 'selfServiceChannel',
} as const;

/**
 * Motivos de recusa (`details.reason`) PRÓPRIOS dos canais Stripe/Play — os
 * comuns (e-mail não verificado, já é membro, país excluído…) continuam em
 * `SelfServiceErrorReason`.
 *
 * Origem: #969 — 2026-10-14 13:37 +0200.
 *
 * Por quê: separado de `SelfServiceErrorReason` de propósito. Aquele enum vai
 * no contrato do iOS, cujo teste exige tradução para todo valor; o iOS não
 * vende pela Stripe nem pela Play e não deve mudar por causa destes canais
 * (owner, 2026-10-14: "não mexer no iOS").
 *
 * Usado por: Functions `selfService/*`, `play/*`; web `apps/app` (paywall) e
 * Android (mapeamento de erro).
 */
export const SelfServiceCheckoutErrorReason = {
  /**
   * A zona do país detectado não tem Price da Stripe no modo atual (o ADMIN do
   * CRM ainda não salvou o preço). Nunca cobra valor errado: recusa.
   */
  PRICE_NOT_CONFIGURED: 'price_not_configured',
  /** Compra Play sem `obfuscatedExternalAccountId` ou com o de outra reserva. */
  PLAY_ACCOUNT_MISMATCH: 'play_account_mismatch',
} as const;
export type SelfServiceCheckoutErrorReason =
  (typeof SelfServiceCheckoutErrorReason)[keyof typeof SelfServiceCheckoutErrorReason];

// ---------- Google Play ----------

/** Produto de assinatura no Play Console (owner cadastra; ver #969 §0). */
export const PLAY_ALL_IN_ONE_PRODUCT_ID = 'allinone_monthly';
/** Base plan mensal do produto. */
export const PLAY_ALL_IN_ONE_BASE_PLAN_ID = 'monthly';
/** Oferta de trial (7 dias) do base plan. */
export const PLAY_TRIAL_OFFER_ID = 'trial-7d';
/** Package do app na Play (`applicationId`). */
export const PLAY_PACKAGE_NAME = 'com.etiquetachef.app';

/**
 * Produtos "slot" do estabelecimento adicional na Play. São o mesmo plano, com
 * o mesmo base plan `monthly` e **sem** a oferta `trial-7d`.
 *
 * Origem: #970 — 2026-10-14 16:58 +0200.
 *
 * Por quê: uma conta Google não compra de novo um produto de assinatura que
 * ela já tem (`ITEM_ALREADY_OWNED`). Por isso cada estabelecimento adicional
 * pago pela Play usa um produto próprio. O servidor escolhe o 1º slot livre
 * na reserva (`reserveSelfServiceTenant`). O cadastro no Play Console é feito
 * à mão pelo owner; enquanto um slot não existir, a compra dele falha na Play
 * e o Android oferece a Stripe.
 *
 * Usado por: Functions `reserveSelfServiceTenant` (escolha),
 * `activatePlayPurchase` (aceite), `playPricingSync` (preço); Android
 * `PaywallViewModel`.
 */
export const PLAY_ADDITIONAL_SLOT_PRODUCT_IDS = [
  'allinone_monthly_slot2',
  'allinone_monthly_slot3',
  'allinone_monthly_slot4',
  'allinone_monthly_slot5',
  'allinone_monthly_slot6',
] as const;

/** É um produto all-in-one da Play (o base ou um slot do adicional)? (#970) */
export function isPlayAllInOneProductId(productId: string | null | undefined): boolean {
  if (!productId) return false;
  const slots: readonly string[] = PLAY_ADDITIONAL_SLOT_PRODUCT_IDS;
  return productId === PLAY_ALL_IN_ONE_PRODUCT_ID || slots.includes(productId);
}

/** `external_subscription_id` da assinatura Play: hash do purchaseToken (cabe no regex). */
export function googlePlaySubscriptionId(purchaseTokenSha256Hex: string): string {
  if (!/^[a-f0-9]{40,64}$/.test(purchaseTokenSha256Hex)) throw new Error('hash do purchaseToken inválido');
  return `gps_${purchaseTokenSha256Hex.slice(0, 40)}`;
}

/** Entrada de `activatePlayPurchase` — o que o BillingClient devolve na compra. */
export const ActivatePlayPurchaseInput = z
  .object({
    purchaseToken: z.string().min(10).max(4096),
    productId: z.string().min(1).max(200),
  })
  .strict();
export type ActivatePlayPurchaseInput = z.infer<typeof ActivatePlayPurchaseInput>;

export const ActivatePlayPurchaseOutput = z.object({
  tenantId: z.string().min(1),
  alreadyActive: z.boolean(),
  /**
   * #1078 (F2 da #960) — região onde o tenant vive. Opcional: servidor antigo não a devolve e o
   * cliente trata ausente como `southamerica-east1`. Os clientes passam a usá-la na F5 para abrir
   * o banco/Functions do tenant sem esperar o diretório (#1081-#1083).
   */
  region: TenantRegionSchema.optional(),
});
export type ActivatePlayPurchaseOutput = z.infer<typeof ActivatePlayPurchaseOutput>;

/**
 * Corpo da Real-time Developer Notification (Pub/Sub `play-rtdn`), já
 * decodificado do base64 de `message.data`. Só `subscriptionNotification`
 * importa; `testNotification`/`oneTimeProductNotification`/
 * `voidedPurchaseNotification` são aceitos e ignorados (ou tratados à parte).
 *
 * Dados (Play Console, formato documentado em developer.android.com/google/play/billing/rtdn-reference):
 *   { "version": "1.0", "packageName": "com.etiquetachef.app",
 *     "eventTimeMillis": "1790400000000",
 *     "subscriptionNotification": { "version": "1.0", "notificationType": 4,
 *       "purchaseToken": "<token>", "subscriptionId": "allinone_monthly" } }
 * `eventTimeMillis` vem como STRING. O estado nunca é tirado daqui: o handler
 * relê `purchases.subscriptionsv2.get` (a notificação só diz "mudou").
 */
export const PlayRtdnSchema = z.object({
  version: z.string().optional(),
  packageName: z.string().min(1),
  eventTimeMillis: z.union([z.string(), z.number()]).transform((v) => Number(v)),
  subscriptionNotification: z
    .object({
      version: z.string().optional(),
      notificationType: z.number().int(),
      purchaseToken: z.string().min(10),
      subscriptionId: z.string().optional(),
    })
    .optional(),
  voidedPurchaseNotification: z
    .object({
      purchaseToken: z.string().min(10),
      orderId: z.string().optional(),
      productType: z.number().int().optional(),
    })
    .optional(),
  testNotification: z.object({ version: z.string().optional() }).optional(),
});
export type PlayRtdn = z.infer<typeof PlayRtdnSchema>;

/** `notificationType` de assinatura (RTDN). Lista de documentação, não enum fechado no wire. */
export const PLAY_SUBSCRIPTION_NOTIFICATION = {
  RECOVERED: 1,
  RENEWED: 2,
  CANCELED: 3,
  PURCHASED: 4,
  ON_HOLD: 5,
  IN_GRACE_PERIOD: 6,
  RESTARTED: 7,
  PRICE_CHANGE_CONFIRMED: 8,
  DEFERRED: 9,
  PAUSED: 10,
  PAUSE_SCHEDULE_CHANGED: 11,
  REVOKED: 12,
  EXPIRED: 13,
  PENDING_PURCHASE_CANCELED: 20,
} as const;
